import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Paper,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import MathRenderer from '../common/MathRenderer';
import api from '../../services/api';
import { DETAIL_ANSWERS, QUESTION_REVIEWS, QUESTION_UPDATE_STATUS, resolveImageDisplayUrl } from '../../config/Config';

const QuestionPreviewDialog = ({ open, onClose, question, onStatusUpdate }) => {
  const [status, setStatus] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailAnswer, setDetailAnswer] = useState(null);
  const [commentHistory, setCommentHistory] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Pre-load data when question changes, before dialog opens
  useEffect(() => {
    if (question && !isDataReady) {
      const loadData = async () => {
        try {
          // Load both detail answer and comment history in parallel
          const [detailResponse, commentsResponse] = await Promise.all([
            api.get(`${DETAIL_ANSWERS}?question_id=${question.id}`),
            api.get(`${QUESTION_REVIEWS}${question.id}/reviews/`)
          ]);

          // Set detail answer
          if (detailResponse.data && detailResponse.data.length > 0) {
            setDetailAnswer(detailResponse.data[0]);
          } else {
            setDetailAnswer(null);
          }

          // Set comment history
          setCommentHistory(commentsResponse.data || []);

          // Mark data as ready
          setIsDataReady(true);
        } catch (error) {
          console.error('Error pre-loading data:', error);
          setDetailAnswer(null);
          setCommentHistory([]);
          setIsDataReady(true);
        }
      };

      loadData();
    }
  }, [question, isDataReady]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open && question && isDataReady) {
              setStatus(question.status_display || '');
      setComment('');
      setError('');
    } else if (!open) {
      // Reset when closing
      setIsDataReady(false);
      setDetailAnswer(null);
      setCommentHistory([]);
      setStatus('');
      setComment('');
      setError('');
    }
  }, [open, question, isDataReady]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    setError('');
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleSubmit = async () => {
    if (!status) {
      setError('Please select a status');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`${QUESTION_UPDATE_STATUS}${question.id}/update_status/`, {
        status: status,
        comment: comment
      });

      enqueueSnackbar(response.data.detail, { variant: 'success' });
      
      // Call the callback to refresh the question list
      if (onStatusUpdate) {
        onStatusUpdate(question.id, response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating question status:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update question status';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    // Map common status values to colors, fallback to default
    const statusColors = {
      'Published': 'success',
      'Rejected': 'error',
      'Draft': 'warning',
      'Pending': 'info'
    };
    return statusColors[status] || 'default';
  };

  const getStatusBgColor = (status) => {
    if (!status) return 'grey.100';
    // Map common status values to background colors, fallback to grey
    const statusBgColors = {
      'Published': 'success.light',
      'Rejected': 'error.light',
      'Draft': 'warning.light',
      'Pending': 'info.light'
    };
    return statusBgColors[status] || 'grey.100';
  };

  const formatOptions = (options) => {
    if (!options || !Array.isArray(options)) return [];
    return options;
  };

  if (!question) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Question Preview & Review
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Show loading only if data is not ready yet */}
          {!isDataReady ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading question details...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Question Details Section */}
              <Typography variant="h6" gutterBottom>
                Question Details
              </Typography>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Question Text:
                    </Typography>
                    <MathRenderer 
                      content={question.question_text}
                      variant="body1"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {(question.image_proxy_url || question.image_url) && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Question Image:
                      </Typography>
                      <img 
                        src={resolveImageDisplayUrl(question.image_proxy_url || question.image_url)} 
                        alt="question" 
                        style={{ 
                          maxWidth: '300px', 
                          maxHeight: '200px', 
                          objectFit: 'contain',
                          borderRadius: '4px',
                          marginTop: '8px'
                        }}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Options:
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {formatOptions(question.options).map((opt, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1, 
                            bgcolor: opt === question.correct_answer ? 'success.light' : 'grey.50',
                            border: opt === question.correct_answer ? '1px solid' : '1px solid transparent',
                            borderColor: opt === question.correct_answer ? 'success.main' : 'transparent'
                          }}
                        >
                          <Typography variant="body2" sx={{ mr: 1, fontWeight: 'bold' }}>
                            {idx + 1}.
                          </Typography>
                          <MathRenderer 
                            content={opt}
                            variant="body2"
                            flexGrow={1}
                          />
                          {/* Removed the green "Correct" chip to avoid double green shading */}
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {!question.has_subquestions && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Correct Answer:
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {question.correct_answer}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show sub-questions if question has sub-questions */}
                  {question.has_subquestions && question.sub_questions && question.sub_questions.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Sub-Questions:
                      </Typography>
                      <Stack spacing={2} sx={{ mt: 1 }}>
                        {question.sub_questions.map((subQ) => (
                          <Paper key={subQ.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ fontWeight: 600, mb: 1 }}>
                              <MathRenderer
                                content={(subQ.letter || String.fromCharCode(97 + question.sub_questions.indexOf(subQ))) + '. ' + (subQ.question_text || '')}
                                variant="body2"
                              />
                            </Box>
                            {subQ.options && subQ.options.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Options:</Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {subQ.options.map((opt, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        bgcolor: opt === subQ.correct_answer ? 'success.light' : 'grey.50',
                                        border: opt === subQ.correct_answer ? '1px solid' : '1px solid transparent',
                                        borderColor: opt === subQ.correct_answer ? 'success.main' : 'transparent'
                                      }}
                                    >
                                      <MathRenderer content={`${idx + 1}. ${opt}`} variant="body2" />
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Correct Answer: </Typography>
                              <MathRenderer content={subQ.correct_answer || ''} variant="body2" sx={{ fontWeight: 600 }} />
                            </Box>
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Marks: </Typography>
                              <Typography variant="body2" component="span">
                                {subQ.marks}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Status:
                    </Typography>

                    <Chip
                      label={question.status_display || 'Not Set'}
                      size="small"
                              color={getStatusColor(question.status_display)}
        sx={{ 
          backgroundColor: getStatusBgColor(question.status_display),
                        fontWeight: 'medium',
                        mt: 1
                      }}
                    />
                  </Box>

                  {/* Comment History */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Comment History:
                    </Typography>
                    {commentHistory.length > 0 ? (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {commentHistory.map((review, idx) => (
                          <Box 
                            key={review.id || idx} 
                            sx={{ 
                              p: 1.5, 
                              borderRadius: 1, 
                              bgcolor: 'grey.50',
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(review.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            {review.comment && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {review.comment}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Reviewed by: {review.reviewer_name || 'Unknown'}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        No review comments yet.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>

              {/* Detail Answer Section */}
              <Typography variant="h6" gutterBottom>
                Detail Answer
              </Typography>
              <Paper sx={{ p: 2, mb: 3 }}>
                {detailAnswer ? (
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Detailed Explanation:
                      </Typography>
                      <MathRenderer 
                        content={detailAnswer.explanation || 'No explanation provided'}
                        variant="body1"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                    {detailAnswer.steps && detailAnswer.steps.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Solution Steps:
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {detailAnswer.steps.map((step, idx) => (
                            <Box key={idx} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                              <MathRenderer 
                                content={step}
                                variant="body1"
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No detail answer available for this question.
                  </Typography>
                )}
              </Paper>

              {/* Status Update Section */}
              <Typography variant="h6" gutterBottom>
                Publish to Platform?
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="status-select-label">New Status</InputLabel>
                                         <Select
                       labelId="status-select-label"
                       id="status-select"
                       value={status}
                       label="New Status"
                       onChange={handleStatusChange}
                     >
                       <MenuItem value="Published">Published</MenuItem>
                       <MenuItem value="Rejected">Rejected</MenuItem>
                     </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Review Comment (Optional)"
                    value={comment}
                    onChange={handleCommentChange}
                    placeholder="Add any comments or feedback about this question..."
                    helperText="Comments will be stored in the question review history"
                  />

                  {error && (
                    <Alert severity="error">
                      {error}
                    </Alert>
                  )}
                </Stack>
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !status}
        >
          {loading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionPreviewDialog;
